import React from 'react';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import Select from '../../../components/ui/Select';


const ClassificationSection = ({ 
  expanded, 
  onToggle, 
  formData, 
  onClassificationChange,
  onSeverityChange,
  setFormData,
  showDynamicFields 
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Incident Classification <span className="text-red-600">*</span>
          </h2>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </div>
      {expanded && (
        <div className="p-4 pt-0 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incident Type <span className="text-red-600">*</span>
              </label>
              <Select
                value={formData?.classification}
                onChange={(val) => onClassificationChange(val)}
                placeholder="Select incident type"
                required
                options={[
                  { value: 'Near Miss', label: 'Near Miss' },
                  { value: 'First Aid Case', label: 'First Aid Case' },
                  { value: 'Medical Treatment Case', label: 'Medical Treatment Case' },
                  { value: 'Restricted Work Case', label: 'Restricted Work Case' },
                  { value: 'Lost Time Injury', label: 'Lost Time Injury' },
                  { value: 'Permanent Disability', label: 'Permanent Disability' },
                  { value: 'Fatality', label: 'Fatality' },
                  { value: 'Property Damage', label: 'Property Damage' },
                  { value: 'Environmental Incident', label: 'Environmental Incident' },
                  { value: 'Vehicle Incident', label: 'Vehicle Incident' },
                ]}
              />
              <p className="mt-1 text-xs text-gray-500">
                Select the type of incident that occurred
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity Level (OSHA Standard) <span className="text-red-600">*</span>
              </label>
              <Select
                value={formData?.severity}
                onChange={(val) => {
                  setFormData(prev => ({ ...prev, severity: val }));
                  if (onSeverityChange) onSeverityChange(val);
                }}
                placeholder="Select severity level"
                required
                options={[
                  { value: 'Near Miss', label: 'Near Miss' },
                  { value: 'Minor Injury', label: 'Minor Injury (First Aid Treatment)' },
                  { value: 'Major Injury', label: 'Major Injury (Medical Treatment)' },
                  { value: 'Lost Time Injury', label: 'Lost Time Injury (LTI)' },
                  { value: 'Permanent Partial Disability', label: 'Permanent Partial Disability' },
                  { value: 'Permanent Total Disability', label: 'Permanent Total Disability' },
                  { value: 'Fatality', label: 'Fatality' },
                ]}
              />
              <p className="mt-1 text-xs text-gray-500">
                OSHA recordable injury classification
              </p>
            </div>

            {(formData?.severity === 'Lost Time Injury' || 
              formData?.severity === 'Permanent Partial Disability' || 
              formData?.severity === 'Permanent Total Disability') && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Days Away from Work <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData?.daysAway || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, daysAway: e?.target?.value }))}
                  placeholder="Enter number of days"
                  required
                />
              </div>
            )}

            {showDynamicFields?.injuryDetails && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type of Injury <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData?.injuryType}
                    onChange={(val) => setFormData(prev => ({ ...prev, injuryType: val }))}
                    placeholder="Select injury type"
                    required
                    options={[
                      { value: 'Laceration', label: 'Laceration' },
                      { value: 'Fracture', label: 'Fracture' },
                      { value: 'Burn', label: 'Burn' },
                      { value: 'Strain/Sprain', label: 'Strain/Sprain' },
                      { value: 'Contusion', label: 'Contusion' },
                      { value: 'Amputation', label: 'Amputation' },
                      { value: 'Crush Injury', label: 'Crush Injury' },
                      { value: 'Chemical Exposure', label: 'Chemical Exposure' },
                      { value: 'Heat Stress', label: 'Heat Stress' },
                      { value: 'Other', label: 'Other' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Body Part Affected <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData?.bodyPart}
                    onChange={(val) => setFormData(prev => ({ ...prev, bodyPart: val }))}
                    placeholder="Select body part"
                    required
                    options={[
                      { value: 'Head', label: 'Head' },
                      { value: 'Eyes', label: 'Eyes' },
                      { value: 'Face', label: 'Face' },
                      { value: 'Neck', label: 'Neck' },
                      { value: 'Shoulder', label: 'Shoulder' },
                      { value: 'Arm', label: 'Arm' },
                      { value: 'Hand', label: 'Hand' },
                      { value: 'Fingers', label: 'Fingers' },
                      { value: 'Back', label: 'Back' },
                      { value: 'Chest', label: 'Chest' },
                      { value: 'Abdomen', label: 'Abdomen' },
                      { value: 'Leg', label: 'Leg' },
                      { value: 'Knee', label: 'Knee' },
                      { value: 'Ankle', label: 'Ankle' },
                      { value: 'Foot', label: 'Foot' },
                      { value: 'Multiple', label: 'Multiple Body Parts' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Treatment Required <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData?.medicalTreatment}
                    onChange={(val) => setFormData(prev => ({ ...prev, medicalTreatment: val }))}
                    placeholder="Select treatment level"
                    required
                    options={[
                      { value: 'First Aid Only', label: 'First Aid Only' },
                      { value: 'Medical Treatment', label: 'Medical Treatment' },
                      { value: 'Emergency Room', label: 'Emergency Room' },
                      { value: 'Hospitalization', label: 'Hospitalization' },
                      { value: 'Surgery Required', label: 'Surgery Required' },
                      { value: 'Ongoing Treatment', label: 'Ongoing Treatment' },
                    ]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OSHA Recordable
                  </label>
                  <Select
                    value={formData?.oshaRecordable}
                    onChange={(val) => setFormData(prev => ({ ...prev, oshaRecordable: val }))}
                    placeholder="Select recordability"
                    options={[
                      { value: 'Yes', label: 'Yes - OSHA Recordable' },
                      { value: 'No', label: 'No - Not Recordable' },
                      { value: 'Pending', label: 'Pending Review' },
                    ]}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Must be recorded on OSHA 300 Log
                  </p>
                </div>
              </>
            )}

            {showDynamicFields?.fatalityDetails && (
              <div className="md:col-span-2">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-900 mb-2">
                    Critical Incident - Immediate Regulatory Notification Required
                  </p>
                  <p className="text-sm text-red-800 mb-2">
                    This incident requires immediate notification to OSHA within 8 hours for fatalities 
                    or within 24 hours for in-patient hospitalizations, amputations, or loss of an eye.
                  </p>
                  <p className="text-sm text-red-800">
                    Investigation team will be automatically assigned. Senior management and regulatory 
                    authorities will be notified.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassificationSection;